import { useCallback, useState } from "react";
import { z } from "zod";

import {
  API_ROLE_MAPPING,
  API_ROLE_MAPPING_USER,
} from "../../../../../config/Endpoints.js";
import {
  ApiError,
  GENERIC_ERROR,
  getApi,
  postApi,
} from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import type {
  MutationResult,
  RoleMappingOperation,
  RoleMappingRow,
} from "../types.ts";
import { extractApiMessage } from "../utils/extract-api-message.ts";
import { unwrapApiDataArray } from "../utils/unwrap-api-data.ts";

const roleRowSchema = z
  .object({
    roleId: z.coerce.number().finite(),
    displayName: z.string(),
    assigned: z.boolean().optional().default(false),
  })
  .transform((role): RoleMappingRow => ({
    roleId: role.roleId,
    name: role.displayName,
    assigned: role.assigned,
  }));

function parseRoles(raw: unknown): RoleMappingRow[] {
  const items = unwrapApiDataArray<unknown>(raw);
  return items.flatMap((item) => {
    const parsed = roleRowSchema.safeParse(item);
    return parsed.success ? [parsed.data] : [];
  });
}

function buildRoleOperations(
  checkedItems: Record<number, boolean>,
): RoleMappingOperation[] {
  return Object.entries(checkedItems).map(([id, checked]) => ({
    id: Number(id),
    operation: checked ? "assign" : "remove",
  }));
}

export function useEmployeeRoles() {
  const [roles, setRoles] = useState<RoleMappingRow[]>([]);
  const [checkedItems, setCheckedItems] = useState<Record<number, boolean>>({});
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const fetchRolesForEmployee = useCallback(async (employeeId: string) => {
    setLoading(true);
    setError(null);
    setSaved(false);
    try {
      const url = `${API_ROLE_MAPPING_USER}/${encodeURIComponent(employeeId)}`;
      const { data } = await getApi<unknown>(url, accessToken());
      const parsed = parseRoles(data);
      setRoles(parsed);
      const nextChecked: Record<number, boolean> = {};
      for (const role of parsed) {
        nextChecked[role.roleId] = role.assigned;
      }
      setCheckedItems(nextChecked);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : GENERIC_ERROR;
      setError(msg);
      setRoles([]);
      setCheckedItems({});
    } finally {
      setLoading(false);
    }
  }, []);

  const selectEmployee = useCallback(
    (employeeId: string | null) => {
      setSelectedEmployeeId(employeeId);
      setSaved(false);
      if (!employeeId) {
        setRoles([]);
        setCheckedItems({});
        return;
      }
      void fetchRolesForEmployee(employeeId);
    },
    [fetchRolesForEmployee],
  );

  const toggleRole = useCallback((roleId: number, checked: boolean) => {
    setSaved(false);
    setCheckedItems((prev) => ({ ...prev, [roleId]: checked }));
  }, []);

  const saveRoles = useCallback(async (): Promise<MutationResult> => {
    if (!selectedEmployeeId) {
      return { ok: false, message: "Select an employee first." };
    }
    setSubmitting(true);
    try {
      const { data } = await postApi(API_ROLE_MAPPING, accessToken(), {
        userId: Number(selectedEmployeeId),
        roleList: buildRoleOperations(checkedItems),
      });
      setSaved(true);
      return { ok: true, message: extractApiMessage(data) };
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : GENERIC_ERROR;
      return { ok: false, message: msg };
    } finally {
      setSubmitting(false);
    }
  }, [checkedItems, selectedEmployeeId]);

  return {
    roles,
    checkedItems,
    selectedEmployeeId,
    loading,
    submitting,
    error,
    saved,
    selectEmployee,
    toggleRole,
    saveRoles,
  };
}
